import React, { useState, useMemo, useCallback, memo } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Typography,
} from "@mui/material";
import { Input } from "@progress/kendo-react-inputs";

// ────────────────────────────────────────────────
//  Memoized list item
// ────────────────────────────────────────────────
const ChannelListItem = memo(({ channel, isChecked, onToggle }) => {
  return (
    <ListItem
      disablePadding
      sx={{
        "&:hover": { bgcolor: "#f5f5f5" },
      }}
    >
      <Box
        onClick={() => onToggle(channel.SID)}
        sx={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          width: "100%",
          px: 0.5,
          py: 0.15,
        }}
      >
        <Checkbox
          checked={isChecked}
          disableRipple
          size="small"
          sx={{
            p: 0.25,
            color: "#aaa",
            "&.Mui-checked": { color: "#3A3633" },
          }}
        />
        <ListItemText
          primary={channel.name}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: isChecked ? 600 : 400,
            color: "#333",
            fontFamily: '"Roboto", sans-serif',
          }}
          sx={{ my: 0, ml: 0.5 }}
        />
      </Box>
    </ListItem>
  );
});

ChannelListItem.displayName = "ChannelListItem";

// ────────────────────────────────────────────────
const PlanningChannelSelector = ({
  ChannelList,
  lang,
  setFilterChannels,
  filterChannels,
}) => {
  const [searchChannel, setSearchChannel] = useState("");

  const [checkedSIDs, setCheckedSIDs] = useState(() => {
    return new Set((filterChannels || []).map((c) => c.SID));
  });

  const { visibleChannels, checkedCount } = useMemo(() => {
    const q = searchChannel.trim().toLowerCase();
    const searchActive = q.length > 0;
    const visible = [];
    let count = 0;

    for (const channel of ChannelList || []) {
      const isChecked = checkedSIDs.has(channel.SID);
      if (isChecked) count++;
      if (!searchActive || (channel.name || "").toLowerCase().includes(q)) {
        visible.push({ channel, isChecked });
      }
    }
    return { visibleChannels: visible, checkedCount: count };
  }, [ChannelList, checkedSIDs, searchChannel]);

  const handleToggle = useCallback(
    (sid) => {
      setCheckedSIDs((prev) => {
        const next = new Set(prev);
        if (next.has(sid)) {
          next.delete(sid);
        } else {
          next.add(sid);
        }
        const selected = ChannelList.filter((c) => next.has(c.SID));
        setFilterChannels(selected);
        return next;
      });
    },
    [ChannelList, setFilterChannels]
  );

  const handleSearchChange = useCallback((e) => {
    setSearchChannel(e.target.value);
  }, []);

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
      {/* Search */}
      <Input
        value={searchChannel}
        onChange={handleSearchChange}
        placeholder={lang?.search_button_text || "Search"}
        autoFocus
        style={{
          height: 30,
          fontSize: 13,
          width: "100%",
          flexShrink: 0,
        }}
      />

      {/* Channel list */}
      <Box
        sx={{
          // flex: 1,
          mt: 0.75,
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          overflow: "hidden",
          bgcolor: "#fff",
          height: "350px",
          overflow: "auto"
        }}
      >
        {visibleChannels.length === 0 ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#999", fontSize: 13 }}
            >
              No channels found
            </Typography>
          </Box>
        ) : (
          <List
            sx={{
              height: "100%",
              overflow: "auto",
              py: 0,
            }}
          >
            {visibleChannels.map(({ channel, isChecked }) => (
              <ChannelListItem
                key={channel.SID}
                channel={channel}
                isChecked={isChecked}
                onToggle={handleToggle}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Count */}
      {checkedCount > 0 && (
        <Typography
          sx={{
            mt: 0.5,
            fontSize: 11,
            color: "#888",
          }}
        >
          {checkedCount} channel{checkedCount !== 1 ? "s" : ""} selected
        </Typography>
      )}
    </Box>
  );
};

export default PlanningChannelSelector;